/**
 * Cost Caps — the per-run LLM spend ceilings, one tab under Prompts & Pipelines.
 *
 * These used to live inside Settings → App Defaults, nested in the card titled
 * "Question Extraction (Assignment From PDF)". They were never extraction
 * settings: the per-operation grid covers every operation in the system, and it
 * had grown to dominate a card whose description talks only about the
 * enrichment pass. Filed here instead, next to Pipelines, because a cap is a
 * property of an operation's execution — the same thing the neighbouring tabs
 * configure.
 *
 * The cap is enforced BEFORE each call, not discovered after: once a run's
 * accrued cost reaches the ceiling the next call is refused. Prod extraction run
 * 7792e1f3 made 24 calls for $1.4953 and produced nothing.
 *
 * See docs/FeatureDesigns/PipelineInfra/llm-metrics-observability.md.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { OperationType } from '@padcanvas/shared';
import { OPERATION_TYPES, OPERATION_TYPE_KEYS } from '@padcanvas/shared';
import { InfoTip, NumberInput } from '@shared-ui/components';
import '../settings/Settings.css';

export function CostCaps() {
  const queryClient = useQueryClient();
  // 0 = no ceiling, matching the backend's reading of the setting.
  const [defaultCap, setDefaultCap] = useState<number>(0);
  // Per-operation overrides. An entry present (including 0) wins over the
  // default; absent falls through. '' in the input means "no entry".
  const [perOperation, setPerOperation] = useState<Partial<Record<OperationType, number>>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: adminApi.getSettings });

  useEffect(() => {
    if (settings?.operationCostCapUsd !== undefined) setDefaultCap(settings.operationCostCapUsd);
    if (settings?.operationCostCapsUsd) setPerOperation(settings.operationCostCapsUsd);
  }, [settings]);

  const mutation = useMutation({
    mutationFn: adminApi.updateSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });

  const save = () => {
    setSaveStatus('saving');
    mutation.mutate({
      operationCostCapUsd: defaultCap,
      operationCostCapsUsd: perOperation,
    }, {
      onSuccess: () => { setSaveStatus('saved'); setTimeout(() => setSaveStatus('idle'), 2000); },
      onError: () => { setSaveStatus('error'); setTimeout(() => setSaveStatus('idle'), 3000); },
    });
  };

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Cost Caps</h2>
        <p className="section-description">
          Ceiling on estimated LLM spend for one operation <em>run</em> — one import, one analysis.
          Enforced before each call: once a run reaches its ceiling the next call is refused, rather
          than the overspend being discovered afterwards. Applies per run, not per day or per tenant.
          Changes apply within the backend settings cache TTL (~2 minutes).
        </p>
      </div>

      <div className="rate-limit-tier">
        <h3 className="rate-limit-tier-title">Default</h3>
        <div className="rate-limit-fields">
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Cost cap per run (USD)
              <InfoTip text="Used for any operation without its own override below. 0 = no ceiling. For scale: an import that failed after 24 calls cost $1.50 and produced nothing." />
            </label>
            <NumberInput
              className="rate-limit-input"
              min={0}
              step={0.25}
              value={defaultCap}
              onChange={(n) => { if (n != null) setDefaultCap(n); }}
            />
          </div>
        </div>
      </div>

      <div className="rate-limit-tier">
        <h3 className="rate-limit-tier-title">Per operation</h3>
        <p className="section-description" style={{ marginTop: 0 }}>
          Operations differ by more than an order of magnitude — an import runs an extraction plus
          several enrichment batches, a hint is fractions of a cent — so one number cannot bound both
          usefully. Blank uses the default. An explicit <code>0</code> means no ceiling for that
          operation, without raising the default for everything else.
        </p>
        <div className="cost-cap-grid">
          {OPERATION_TYPE_KEYS.map(op => (
            <label key={op} className="cost-cap-row">
              <span className="cost-cap-op">{OPERATION_TYPES[op].label}</span>
              <input
                type="number"
                className="rate-limit-input"
                min={0}
                step={0.25}
                placeholder={defaultCap ? String(defaultCap) : 'none'}
                value={perOperation[op] ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  setPerOperation(prev => {
                    const next = { ...prev };
                    if (raw === '') delete next[op];
                    else {
                      const n = Number(raw);
                      if (Number.isFinite(n)) next[op] = Math.max(0, n);
                    }
                    return next;
                  });
                }}
              />
            </label>
          ))}
        </div>
      </div>

      <div className="rate-limit-actions">
        <button className="rate-limit-save-btn" onClick={save} disabled={mutation.isPending}>
          {mutation.isPending && saveStatus === 'saving' ? 'Saving...' : 'Save Cost Caps'}
        </button>
        {saveStatus === 'saved' && <span className="status-saved">Cost caps saved!</span>}
        {saveStatus === 'error' && <span className="status-error">Failed to save</span>}
      </div>
    </div>
  );
}
